import React from 'react';
import clsx from 'clsx';
import { Menu, X, PanelRightOpen, PanelRightClose, Store, ExternalLink } from 'lucide-react';
import { IconButton } from '../../ui/IconButton';
import { Button } from '../../ui/Button';

/**
 * Top header + responsive nav (mobile side-sheet + desktop sidebar) for the
 * store admin dashboard.
 *
 * The list of admin tabs lives in the parent (`StoreAdmin`) and is passed
 * in via `tabs` so we don't duplicate the definition. Tabs are visually
 * grouped here (by id) purely for legibility — the grouping is cosmetic and
 * falls back gracefully for any unrecognised tab id.
 */

// Cosmetic grouping. Order within each group follows the order tabs arrive in.
const GROUPS = [
    { key: 'overview',   label: 'סקירה',  ids: ['dashboard', 'analytics'] },
    { key: 'catalog',    label: 'קטלוג',  ids: ['products', 'categories', 'coupons'] },
    { key: 'operations', label: 'תפעול',  ids: ['orders', 'delivery', 'timeslots'] },
    { key: 'settings',   label: 'הגדרות', ids: ['store-details', 'settings'] },
];

function groupTabs(tabs) {
    const byId = new Map(tabs.map((t) => [t.id, t]));
    const used = new Set();
    const groups = GROUPS
        .map((g) => ({
            ...g,
            items: g.ids
                .map((id) => byId.get(id))
                .filter(Boolean)
                .map((t) => (used.add(t.id), t)),
        }))
        .filter((g) => g.items.length > 0);
    const leftovers = tabs.filter((t) => !used.has(t.id));
    if (leftovers.length) groups.push({ key: 'other', label: 'אחר', items: leftovers });
    return groups;
}

function StoreBrand({ store, compact = false }) {
    return (
        <div
            className={clsx(
                'flex items-center gap-2.5 min-w-0 bg-brand-600 rounded-pill',
                compact ? 'p-1' : 'ps-1 pe-3.5 py-1',
            )}
        >
            <div className="w-9 h-9 rounded-pill overflow-hidden shrink-0 bg-white/15 ring-1 ring-white/40 flex items-center justify-center">
                {store?.profile_image ? (
                    <img
                        src={store.profile_image}
                        alt={store?.name || 'חנות'}
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <Store className="w-4.5 h-4.5 text-white" />
                )}
            </div>
            {!compact && (
                <div className="min-w-0">
                    <div className="text-2xs font-semibold tracking-wide text-white/70 uppercase leading-none">
                        ניהול חנות
                    </div>
                    <div className="text-sm font-bold text-white truncate leading-tight mt-0.5">
                        {store?.name || 'החנות שלי'}
                    </div>
                </div>
            )}
        </div>
    );
}

function NavItem({ tab, active, collapsed, onClick }) {
    const Icon = tab.icon;
    return (
        <button
            type="button"
            onClick={onClick}
            aria-current={active ? 'page' : undefined}
            title={collapsed ? tab.label : undefined}
            className={clsx(
                'group relative w-full flex items-center gap-3 rounded-xl text-sm font-semibold',
                'transition-[background-color,color] duration-200',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400',
                collapsed ? 'justify-center px-0 py-3' : 'justify-start px-3 py-2.5',
                active
                    ? 'bg-brand-50 text-brand-800'
                    : 'text-ink-muted hover:bg-surface-muted hover:text-ink',
            )}
        >
            {/* Active accent bar — sits on the inline-start (right in RTL) edge */}
            <span
                className={clsx(
                    'absolute inset-y-1.5 -start-0 w-1 rounded-pill bg-brand-500 transition-opacity duration-200',
                    active ? 'opacity-100' : 'opacity-0',
                )}
                aria-hidden="true"
            />
            <span
                className={clsx(
                    'inline-flex items-center justify-center w-9 h-9 rounded-lg shrink-0 transition-colors duration-200',
                    active
                        ? 'bg-brand-500 text-ink-inverse shadow-sm'
                        : 'bg-surface-muted text-ink-muted group-hover:bg-surface-sunken group-hover:text-ink',
                )}
            >
                <Icon className="w-[18px] h-[18px]" />
            </span>
            {!collapsed && <span className="truncate">{tab.label}</span>}
        </button>
    );
}

function NavGroups({ groups, activeTab, collapsed, onSelect }) {
    return (
        <div className={clsx('flex flex-col', collapsed ? 'gap-1' : 'gap-5')}>
            {groups.map((group, gi) => (
                <div key={group.key} className="flex flex-col gap-1">
                    {collapsed
                        ? gi > 0 && <div className="mx-2 my-1 h-px bg-line-subtle" aria-hidden="true" />
                        : (
                            <div className="px-3 pb-0.5 text-2xs font-bold tracking-wider text-ink-faint uppercase">
                                {group.label}
                            </div>
                        )}
                    {group.items.map((tab) => (
                        <NavItem
                            key={tab.id}
                            tab={tab}
                            active={activeTab === tab.id}
                            collapsed={collapsed}
                            onClick={() => onSelect(tab.id)}
                        />
                    ))}
                </div>
            ))}
        </div>
    );
}

export function StoreAdminNav({
    tabs,
    activeTab,
    onChangeTab,
    onOpenStorePublic,
    showMobileMenu,
    setShowMobileMenu,
    collapsed = false,
    onToggleCollapsed,
    store,
}) {
    const groups = groupTabs(tabs);

    return (
        <>
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-nav bg-surface border-b border-line-subtle shadow-sm h-16">
                <div className="max-w-7xl mx-auto h-full flex items-center justify-between gap-3 px-4">
                    <div className="flex items-center gap-2 min-w-0">
                        <IconButton
                            icon={<Menu />}
                            aria-label="תפריט"
                            variant="ghost"
                            tone="neutral"
                            className="lg:hidden"
                            onClick={() => setShowMobileMenu(!showMobileMenu)}
                        />
                    </div>
                    <Button
                        onClick={onOpenStorePublic}
                        variant="outline"
                        size="sm"
                        icon={<ExternalLink />}
                        iconPosition="end"
                    >
                        צפייה בחנות
                    </Button>
                </div>
            </header>

            {/* Mobile side-sheet */}
            {showMobileMenu && (
                <div
                    className="fixed inset-0 z-modal-back lg:hidden"
                    onClick={() => setShowMobileMenu(false)}
                >
                    <div className="absolute inset-0 bg-ink/45 backdrop-blur-sm animate-fade-in" />
                    <nav
                        className="absolute top-0 bottom-0 right-0 w-[19rem] max-w-[85vw] bg-surface shadow-elevated animate-slide-in-right flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                        aria-label="ניווט ניהול"
                    >
                        <div className="flex items-center justify-between gap-3 px-4 h-16 border-b border-line-subtle shrink-0">
                            <StoreBrand store={store} />
                            <IconButton
                                icon={<X />}
                                aria-label="סגירת תפריט"
                                variant="ghost"
                                tone="neutral"
                                onClick={() => setShowMobileMenu(false)}
                            />
                        </div>
                        <div className="flex-1 overflow-y-auto p-3">
                            <NavGroups
                                groups={groups}
                                activeTab={activeTab}
                                collapsed={false}
                                onSelect={(id) => { onChangeTab(id); setShowMobileMenu(false); }}
                            />
                        </div>
                    </nav>
                </div>
            )}

            {/* Desktop sidebar */}
            <aside
                className={clsx(
                    'hidden lg:flex fixed top-16 right-0 bottom-20 bg-surface border-s border-line-subtle shadow-sm flex-col overflow-x-hidden z-nav',
                    'transition-[width] duration-200',
                    collapsed ? 'w-16' : 'w-56',
                )}
                aria-label="ניווט ניהול"
            >
                <div
                    className={clsx(
                        'flex items-center h-14 shrink-0 border-b border-line-subtle',
                        collapsed ? 'justify-center px-0' : 'justify-between px-3',
                    )}
                >
                    {!collapsed && <StoreBrand store={store} compact />}
                    <button
                        type="button"
                        onClick={onToggleCollapsed}
                        aria-label={collapsed ? 'הרחבת תפריט' : 'צמצום תפריט'}
                        aria-expanded={!collapsed}
                        title={collapsed ? 'הרחבת תפריט' : 'צמצום תפריט'}
                        className={clsx(
                            'flex items-center justify-center w-9 h-9 rounded-lg shrink-0',
                            'text-ink-muted hover:text-ink hover:bg-surface-muted',
                            'transition-colors duration-200',
                            'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400',
                        )}
                    >
                        {collapsed ? <PanelRightOpen className="w-5 h-5" /> : <PanelRightClose className="w-5 h-5" />}
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto overflow-x-hidden p-3">
                    <NavGroups
                        groups={groups}
                        activeTab={activeTab}
                        collapsed={collapsed}
                        onSelect={onChangeTab}
                    />
                </div>
            </aside>
        </>
    );
}

export default StoreAdminNav;
